import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, CreditCard, ShoppingBag, ChevronRight, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuthStore } from '../store/auth.store.js';
import { useClientStore } from '../store/client.store.js';
import { getClients, getStores, getTerminals } from '../api/stats.js';
import { getOrders, getOrderStats } from '../api/orders.js';
import Spinner from '../components/Spinner.jsx';
import SalesLineChart, { PaymentMethodChart } from '../components/SalesLineChart.jsx';
import DayDrillCard from '../components/DayDrillCard.jsx';

/* ── helpers ─────────────────────────────────────────────────── */
const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('es-AR').format(n || 0);

/* ── Stat card ───────────────────────────────────────────────── */
function StatCard({ label, value, icon: Icon, color, to, loading }) {
  const navigate = useNavigate();
  const colorMap = {
    blue:   { box: 'bg-blue-400/10 border-blue-400/20',      icon: 'text-blue-400'    },
    emerald:{ box: 'bg-emerald-400/10 border-emerald-400/20', icon: 'text-emerald-400' },
    violet: { box: 'bg-violet-400/10 border-violet-400/20',  icon: 'text-violet-400'  },
    amber:  { box: 'bg-amber-400/10 border-amber-400/20',    icon: 'text-amber-400'   },
    accent: { box: 'bg-accent/10 border-accent/20',           icon: 'text-accent'      },
  };
  const c = colorMap[color] || colorMap.accent;
  return (
    <button
      onClick={() => to && navigate(to)}
      className={`card text-left w-full transition-all group
        ${to ? 'hover:border-accent/30 hover:shadow-lg cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${c.box}`}>
          <Icon size={20} className={c.icon} />
        </div>
        {to && <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity text-accent" />}
      </div>
      <div className="mt-4">
        {loading
          ? <div className="h-8 flex items-center"><Spinner /></div>
          : <p className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>{value ?? '—'}</p>
        }
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
      </div>
    </button>
  );
}

/* ── Session card ────────────────────────────────────────────── */
function SessionCard({ user }) {
  const initial = (user?.name || user?.email || '?')[0].toUpperCase();
  const roleColor = {
    superadmin: 'bg-violet-400/10 border-violet-400/20 text-violet-400',
    admin:      'bg-sky-400/10 border-sky-400/20 text-sky-400',
  }[user?.role] || 'bg-slate-400/10 border-slate-400/20 text-slate-400';
  return (
    <div className="card flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center shrink-0">
        <span className="text-white font-bold text-lg">{initial}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>{user?.name || user?.email}</p>
        <p className="text-sm truncate" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
      </div>
      <span className={`text-xs px-2 py-1 rounded-lg border font-medium shrink-0 ${roleColor}`}>{user?.role}</span>
    </div>
  );
}

/* ── Quick actions ───────────────────────────────────────────── */
function QuickAction({ icon: Icon, label, to, color = 'var(--text-secondary)' }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="flex items-center gap-3 px-4 py-3 rounded-xl border transition-all hover:border-accent/30 hover:bg-accent/5 text-left group"
      style={{ borderColor: 'var(--border)' }}
    >
      <Icon size={16} style={{ color }} />
      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-accent" />
    </button>
  );
}

/* ── Daily bar chart (SVG) ───────────────────────────────────── */
function DailyChart({ data, loading }) {
  if (loading) return <div className="h-40 flex items-center justify-center"><Spinner /></div>;
  if (!data?.length) return <p className="text-sm text-slate-500 py-8 text-center">Sin datos</p>;

  const maxAmt = Math.max(...data.map(d => parseFloat(d.amount)));
  const W = 560, H = 120, pad = 4;
  const barW = Math.max(4, (W - pad * data.length) / data.length);

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H + 20}`} className="w-full min-w-[300px]" style={{ height: H + 20 }}>
        {data.map((d, i) => {
          const h = maxAmt > 0 ? Math.max(3, (parseFloat(d.amount) / maxAmt) * H) : 3;
          const x = i * (barW + pad);
          const y = H - h;
          const isToday = d.date === new Date().toISOString().slice(0, 10);
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={barW} height={h}
                rx={2} fill={isToday ? 'var(--accent)' : '#334155'}
                className="transition-all hover:opacity-80" />
              <title>{d.date}: {fmt(d.amount)} ({d.count} órd.)</title>
            </g>
          );
        })}
        {/* x-axis labels: first, mid, last */}
        {[0, Math.floor(data.length / 2), data.length - 1].map((idx) => {
          const d = data[idx];
          if (!d) return null;
          const x = idx * (barW + pad) + barW / 2;
          const label = d.date.slice(5); // MM-DD
          return (
            <text key={idx} x={x} y={H + 16} textAnchor="middle"
              fontSize={9} fill="var(--text-muted)">{label}</text>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Status bars ─────────────────────────────────────────────── */
const STATUS_LABELS = { processed: 'Procesadas', canceled: 'Canceladas', expired: 'Expiradas', created: 'Creadas' };
const STATUS_COLORS = { processed: 'bg-emerald-500', canceled: 'bg-red-500', expired: 'bg-slate-500', created: 'bg-amber-500' };

function StatusBars({ data, loading }) {
  if (loading) return <div className="h-20 flex items-center justify-center"><Spinner /></div>;
  if (!data?.length) return <p className="text-sm text-slate-500 py-4 text-center">Sin datos</p>;

  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="space-y-3">
      {data.map((d) => {
        const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
        const color = STATUS_COLORS[d.status] || 'bg-slate-400';
        return (
          <div key={d.status} className="space-y-1">
            <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
              <span>{STATUS_LABELS[d.status] || d.status}</span>
              <span>{fmtNum(d.count)} · {pct}%</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
              <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Month comparison ────────────────────────────────────────── */
function MonthComparison({ thisMonth, lastMonth, loading }) {
  if (loading) return <div className="h-16 flex items-center"><Spinner /></div>;

  const thisAmt  = parseFloat(thisMonth?.amount  || 0);
  const lastAmt  = parseFloat(lastMonth?.amount  || 0);
  const thisCount = thisMonth?.count  || 0;
  const lastCount = lastMonth?.count  || 0;

  const amtDiff  = lastAmt  > 0 ? ((thisAmt  - lastAmt)  / lastAmt  * 100).toFixed(1) : null;
  const cntDiff  = lastCount > 0 ? ((thisCount - lastCount) / lastCount * 100).toFixed(1) : null;
  const up = parseFloat(amtDiff) >= 0;

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Este mes</p>
        <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{fmt(thisAmt)}</p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmtNum(thisCount)} órdenes</p>
      </div>
      <div>
        <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Mes anterior</p>
        <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{fmt(lastAmt)}</p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmtNum(lastCount)} órdenes</p>
      </div>
      {amtDiff !== null && (
        <div className="col-span-2 flex items-center gap-1.5">
          {up
            ? <TrendingUp size={14} className="text-emerald-400" />
            : <TrendingDown size={14} className="text-red-400" />}
          <span className={`text-xs font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>
            {up ? '+' : ''}{amtDiff}% en monto · {cntDiff !== null ? `${parseFloat(cntDiff) >= 0 ? '+' : ''}${cntDiff}% en cantidad` : ''}
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Top POS ─────────────────────────────────────────────────── */
function TopPos({ data, loading }) {
  if (loading) return <div className="h-24 flex items-center justify-center"><Spinner /></div>;
  if (!data?.length) return <p className="text-sm text-slate-500 py-4 text-center">Sin datos</p>;

  const maxAmt = Math.max(...data.map(d => parseFloat(d.amount)));
  return (
    <div className="space-y-2.5">
      {data.map((d, i) => {
        const pct = maxAmt > 0 ? Math.round((parseFloat(d.amount) / maxAmt) * 100) : 0;
        return (
          <div key={d.pos_name} className="space-y-1">
            <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
              <span className="truncate max-w-[60%]">{i + 1}. {d.pos_name}</span>
              <span>{fmt(d.amount)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full bg-accent/70 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────────── */
export default function Dashboard() {
  const navigate          = useNavigate();
  const user              = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const isSuperAdmin      = user?.role === 'superadmin';
  const clientId          = isSuperAdmin ? selectedClientId : user?.clientId;

  const results = useQueries({
    queries: [
      {
        queryKey: ['clients'],
        queryFn: getClients,
        enabled: isSuperAdmin,
      },
      {
        queryKey: ['stores', clientId],
        queryFn: () => getStores(isSuperAdmin ? clientId : null),
        enabled: !!user,
      },
      {
        queryKey: ['terminals', clientId],
        queryFn: () => getTerminals(isSuperAdmin ? clientId : null),
        enabled: !!user,
      },
      {
        queryKey: ['orders', { page: 1 }],
        queryFn: () => getOrders({ page: 1, limit: 1, ...(clientId ? { clientId } : {}) }),
        enabled: !!user,
      },
    ],
  });

  const [clientsQ, storesQ, terminalsQ, ordersQ] = results;

  // Analytics — solo para admin (o superadmin con cliente seleccionado)
  const showAnalytics = !isSuperAdmin || !!clientId;
  const statsQ = useQuery({
    queryKey: ['order-stats', clientId],
    queryFn: () => getOrderStats(isSuperAdmin ? clientId : null),
    enabled: showAnalytics && !!user,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Activity size={18} className="text-accent" />
        </div>
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Dashboard</h2>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Resumen del sistema</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className={`grid gap-4 ${isSuperAdmin ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3'}`}>
        {isSuperAdmin && (
          <StatCard label="Clientes" value={clientsQ.data?.length} loading={clientsQ.isLoading}
            icon={Building2} color="violet" to="/clients" />
        )}
        <StatCard label="Sucursales" value={storesQ.data?.length} loading={storesQ.isLoading}
          icon={MapPin} color="emerald" to="/stores" />
        <StatCard label="Terminales" value={terminalsQ.data?.length} loading={terminalsQ.isLoading}
          icon={CreditCard} color="amber" to="/terminals" />
        <StatCard label="Órdenes totales" value={ordersQ.data?.total} loading={ordersQ.isLoading}
          icon={ShoppingBag} color="accent" to="/orders" />
      </div>

      {/* Analytics — solo si hay datos del cliente */}
      {showAnalytics && (
        <>
          {/* Comparativa mensual + Top POS + Drilldown del día */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card space-y-4">
              <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>COMPARATIVA MENSUAL</p>
              <MonthComparison
                thisMonth={statsQ.data?.thisMonth}
                lastMonth={statsQ.data?.lastMonth}
                loading={statsQ.isLoading}
              />
            </div>
            <div className="card space-y-4">
              <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>TOP POS POR MONTO</p>
              <TopPos data={statsQ.data?.byPos} loading={statsQ.isLoading} />
            </div>
            <DayDrillCard clientId={clientId} isSuperAdmin={isSuperAdmin} />
          </div>

          {/* Gráfico de línea interactivo */}
          <SalesLineChart clientId={clientId} isSuperAdmin={isSuperAdmin} />
          <PaymentMethodChart clientId={clientId} isSuperAdmin={isSuperAdmin} />

          {/* Estado de órdenes */}
          <div className="card space-y-4">
            <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>DISTRIBUCIÓN POR ESTADO</p>
            <StatusBars data={statsQ.data?.byStatus} loading={statsQ.isLoading} />
          </div>
        </>
      )}

      {/* Bottom row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SessionCard user={user} />
        <div className="card space-y-1 !p-3">
          <p className="text-xs font-semibold px-1 mb-2" style={{ color: 'var(--text-muted)' }}>ACCESO RÁPIDO</p>
          <QuickAction icon={ShoppingBag} label="Ver órdenes recientes"  to="/orders"    color="#00b1ea" />
          <QuickAction icon={MapPin}      label="Gestionar sucursales"   to="/stores"    color="#34d399" />
          <QuickAction icon={CreditCard}  label="Gestionar terminales"   to="/terminals" color="#fbbf24" />
        </div>
      </div>
    </div>
  );
}
